import { type FormEvent, useState } from "react";

import { api } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { useLang } from "../lib/i18n";
import { authCopy } from "../lib/i18n/auth";
import { ScreenShell } from "./screen-shell";
import { Eyebrow, FormField, H1, Input, MonoMeta, PrimaryButton } from "./ui/primitives";
import { SytoLogo } from "./ui/syto-logo";

// Reset-password (apply new password from link). Visual parity with
// B_ResetPwd in design_handoff_sytofood/screens-b2.jsx (~line 271-310):
// logo, eyebrow, editorial display heading, mono-styled password fields
// matching SignIn pattern, tangerine CTA. There is no "Back" affordance
// because the user came in via a magic link; success returns to the app.

type Props = {
  code: string;
  onDone: () => void;
};

export function ResetPasswordScreen({ code, onDone }: Props) {
  const { refresh } = useAuth();
  const { lang } = useLang();
  const t = authCopy[lang];
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError(t.rpPasswordMismatch);
      return;
    }
    setSubmitting(true);
    try {
      await api.confirmPasswordReset(code, password);
      await refresh();
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.genericError);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ScreenShell hideHeader>
      <div style={{ marginBottom: 20 }}>
        <SytoLogo size={22} />
      </div>

      <div style={{ marginTop: 28 }}>
        <Eyebrow>{t.rpEyebrow}</Eyebrow>
        <H1 accent="." className="mt-3">
          {t.rpHeading}
        </H1>
        <p
          style={{
            marginTop: 14,
            fontSize: 14,
            color: "var(--ink-2)",
            lineHeight: 1.5,
          }}
        >
          {t.rpIntro}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3" style={{ marginTop: 24 }}>
        <FormField label={t.rpNewPasswordLabel} hint={t.rpNewPasswordHint}>
          <Input
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            style={{ fontFamily: "var(--font-mono)", letterSpacing: "0.1em" }}
          />
        </FormField>

        <FormField label={t.rpConfirmLabel}>
          <Input
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="••••••••"
            style={{ fontFamily: "var(--font-mono)", letterSpacing: "0.1em" }}
          />
        </FormField>

        {error ? (
          <p
            className="text-[12px] px-3 py-2 rounded-md"
            style={{ background: "var(--bad-bg)", color: "var(--bad-fg)" }}
          >
            {error}
          </p>
        ) : null}

        <PrimaryButton
          type="submit"
          disabled={submitting || password.length < 8 || confirm.length < 8}
          className="mt-2"
        >
          {submitting ? t.rpSubmitting : t.rpSubmit}
        </PrimaryButton>
      </form>

      <div
        style={{
          marginTop: 18,
          padding: "12px 14px",
          borderRadius: 10,
          background: "var(--surface-2)",
        }}
      >
        <MonoMeta>{t.rpHintLabel}</MonoMeta>
        <div style={{ marginTop: 6, fontSize: 12, color: "var(--ink-2)" }}>
          {t.rpHintBody}
        </div>
      </div>
    </ScreenShell>
  );
}
